import React, { useContext } from 'react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { Row, Col, DatePicker, InputNumber, Select, Input, Button } from 'antd';
import dayjs from 'dayjs';
import FormLegend from '../Forms/FormLegend';
import WorkOrderDetailsContext from '../../containers/WorkOrder/WorkOrderDetailsContext';
import ImageUploader from 'containers/WorkOrder/ImageUploadContainer';
import DocumentUploader from 'containers/WorkOrder/DocumentUploader';
import { AppStore, ShopStore } from 'stores';
import { createSafeS3Url } from 'shared/utils';
import { customFleets } from 'shared';
import { PlusOutlined } from '@ant-design/icons';
// import SelectContactTable from 'components/Contacts/SelectContactTable';

const equalHeightStyle = {
  minHeight: '321px',
};

const documentUploaderStyle = {
  border: '1px solid #d9d9d9',
  borderRadius: '2px',
  padding: '24px',
  marginTop: '15px',
  marginBottom: '24px',
  ...equalHeightStyle,
  maxHeight: '321px',
  overflow: 'auto',
};

const WorkOrderOptionalInformation = ({ forScan = [] }) => {
  const {
    state: {
      initialFormValues,
      isDisabled,
      currentWorkOrder,
      shopVendors,
      purchaseOrders,
      wo_images,
      wo_documents,
    },
    setInitialFormValue,
    setImageUploaderContainerRef,
    setDocumentUploaderContainerRef,
    handleImageUpload,
    handleFileUpload,
    onClickImage,
    onClickAddPurchaseOrder,
    handleFormChange,
  } = useContext(WorkOrderDetailsContext);

  const filterOption = (input, option) => {
    return (
      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
    );
  };

  const handleDocumentPreview = async (file) => {
    try {
      AppStore.addLoading('Getting document preview');

      if (file.originFileObj) {
        // If we have the original file object, use it directly
        onClickImage(file.originFileObj);
      } else if (file.url) {
        const encodedUrl = createSafeS3Url(file.url);
        const response = await axios.get(encodedUrl, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {
          type: file.type || 'application/pdf',
        });
        const blobUrl = URL.createObjectURL(blob);
        onClickImage(blobUrl);
      } else {
        // Fallback: try to use the file object as is
        onClickImage(file);
      }
    } catch (error) {
      console.error('Error getting document preview', error);
      AppStore.addError(
        'Unable to preview document, please try re-uploading it.'
      );
      Sentry.captureException(error, {
        tags: {
          component: 'WorkOrderOptionalInformation',
          method: 'handleDocumentPreview',
        },
        extra: {
          url: file.url,
        },
      });
    } finally {
      AppStore.removeLoading('Getting document preview');
    }
  };

  /**
   * @param {object|null} whatChanged - The data that changed. If null, it'll just flag that the form changed.
   */
  const handleChange = (whatChanged = null) => {
    if (handleFormChange) {
      handleFormChange(whatChanged);
    }
  };

  return (
    <>
      <FormLegend>Optional Details</FormLegend>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <label>Purchase Order:</label>
          <Select
            value={initialFormValues?.purchase_order_id}
            onChange={(value) => {
              handleChange({ purchase_order_id: value ?? null });
              setInitialFormValue('purchase_order_id', value ?? null);
            }}
            style={{ width: '100%' }}
            placeholder="Select a Purchase Order"
            disabled={isDisabled}
            allowClear
            data-testid="purchase-order-select"
          >
            {purchaseOrders.map((purchaseOrder) => {
              let poName = `#${purchaseOrder.purchase_order_number}`;
              if (purchaseOrder.reference_number) {
                poName += ` - ${purchaseOrder.reference_number}`;
              }
              return (
                <Select.Option key={purchaseOrder.id} value={purchaseOrder.id}>
                  {poName}
                </Select.Option>
              );
            })}
          </Select>
          <Button
            style={{ padding: 0 }}
            type="link"
            icon={<PlusOutlined />}
            onClick={() => {
              handleChange();
              onClickAddPurchaseOrder();
            }}
            data-testid="add-purchase-order-button"
          >
            Add Purchase Order
          </Button>
        </Col>
        <Col span={8}>
          <label>Issue report date:</label>
          <DatePicker
            value={
              initialFormValues?.woIssueReportDate
                ? dayjs(initialFormValues?.woIssueReportDate)
                : null
            }
            onChange={(date) => {
              handleChange({ woIssueReportDate: date });
              setInitialFormValue('woIssueReportDate', date);
            }}
            format={'MM/DD/YYYY HH:mm:ss'}
            style={{ width: '100%' }}
            showTime
            placeholder="Select date and time"
            disabled={isDisabled}
            data-testid="issue-report-date-select"
          />
        </Col>
        <Col span={8}>
          <label>Work Order Start:</label>
          <DatePicker
            format={'MM/DD/YYYY HH:mm:ss'}
            value={
              initialFormValues?.woStartDate
                ? dayjs(initialFormValues?.woStartDate)
                : null
            }
            onChange={(date) => {
              handleChange({ woStartDate: date });
              setInitialFormValue('woStartDate', date);
            }}
            style={{ width: '100%' }}
            showTime
            placeholder="Select date and time"
            disabled={isDisabled}
            data-testid="work-order-start-select"
          />
        </Col>
        <Col span={8}>
          <label>Work Order Completed:</label>
          <DatePicker
            format={'MM/DD/YYYY HH:mm:ss'}
            value={
              initialFormValues?.woCompletedDate
                ? dayjs(initialFormValues?.woCompletedDate)
                : null
            }
            onChange={(date) => {
              handleChange({ woCompletedDate: date });
              setInitialFormValue('woCompletedDate', date);
            }}
            style={{ width: '100%' }}
            showTime
            placeholder="Select date and time"
            disabled={isDisabled}
            data-testid="work-order-completed-select"
          />
        </Col>
        <Col span={12}>
          <label>Mileage:</label>
          <InputNumber
            value={initialFormValues?.mileage}
            onChange={(value) => {
              handleChange({ mileage: value });
              setInitialFormValue('mileage', value);
            }}
            style={{ width: '100%' }}
            placeholder="Mileage"
            disabled={isDisabled}
            inputMode="tel"
            data-testid="mileage-input"
          />
        </Col>
        <Col span={12}>
          <label>Engine Hours:</label>
          <InputNumber
            value={initialFormValues?.engineHours}
            onChange={(value) => {
              handleChange({ engineHours: value });
              setInitialFormValue('engineHours', value);
            }}
            style={{ width: '100%' }}
            placeholder="Engine Hours"
            disabled={isDisabled}
            inputMode="tel"
            data-testid="engine-hours-input"
          />
        </Col>
        {!customFleets.ufpFleet.includes(
          ShopStore.currentShop?.parentShopId || ShopStore.currentShop?.id
        ) && (
          <>
            <Col span={24}>
              <label>Vendor:</label>
              <Select
                style={{ width: '100%' }}
                value={initialFormValues?.vendor_id}
                onChange={(value) => {
                  handleChange({ vendor_id: value });
                  setInitialFormValue('vendor_id', value);
                }}
                placeholder="Select a User"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                isDisabled={isDisabled}
              >
                {(shopVendors ?? []).map((vendor) => (
                  <Select.Option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={24}>
              <label>Additional Notes:</label>
              <Input.TextArea
                value={initialFormValues?.additionalNotes}
                onChange={(e) => {
                  handleChange({ additionalNotes: e.target.value });
                  setInitialFormValue('additionalNotes', e.target.value);
                }}
                style={{ width: '100%' }}
                placeholder="Additional Notes"
                disabled={isDisabled}
                rows={6}
              />
            </Col>
          </>
        )}
      </Row>

      {customFleets.ufpFleet.includes(
        ShopStore.currentShop?.parentShopId || ShopStore.currentShop?.id
      ) && (
        <Row
          className="limit-size-tablets"
          style={{ marginTop: '20px' }}
          gutter={[16, 16]}
        >
          <Col span={24}>
            <FormLegend>Driver Report Details</FormLegend>
          </Col>
          <Col span={8}>
            <label>Driver Name:</label>
            <Input
              value={initialFormValues?.driverReportDetails?.driverName}
              onChange={(e) => {
                const newVal = Object.assign(
                  {},
                  initialFormValues.driverReportDetails,
                  {
                    driverName: e.target.value,
                  }
                );
                handleChange(newVal);
                setInitialFormValue('driverReportDetails', newVal);
              }}
              style={{ width: '100%' }}
              disabled={isDisabled}
              data-testid="driver-name-input"
            />
          </Col>
          <Col span={8}>
            <label>Driver Number:</label>
            <Input
              value={initialFormValues?.driverReportDetails?.driverNumber}
              onChange={(e) => {
                const newVal = Object.assign(
                  {},
                  initialFormValues.driverReportDetails,
                  {
                    driverNumber: e.target.value,
                  }
                );
                handleChange(newVal);
                setInitialFormValue('driverReportDetails', newVal);
              }}
              style={{ width: '100%' }}
              disabled={isDisabled}
              data-testid="driver-number-input"
            />
          </Col>
          <Col span={8}>
            <label>Location:</label>
            <Input
              value={initialFormValues?.driverReportDetails?.location}
              onChange={(e) => {
                const newVal = Object.assign(
                  {},
                  initialFormValues.driverReportDetails,
                  {
                    location: e.target.value,
                  }
                );
                handleChange(newVal);
                setInitialFormValue('driverReportDetails', newVal);
              }}
              style={{ width: '100%' }}
              disabled={isDisabled}
              data-testid="location-input"
            />
          </Col>
          <Col span={12}>
            <label>Time of Event:</label>
            <DatePicker
              value={
                initialFormValues?.driverReportDetails?.timeOfEvent
                  ? dayjs(initialFormValues?.driverReportDetails?.timeOfEvent)
                  : null
              }
              onChange={(date) => {
                const newVal = Object.assign(
                  {},
                  initialFormValues.driverReportDetails,
                  {
                    timeOfEvent: date,
                  }
                );
                handleChange(newVal);
                setInitialFormValue('driverReportDetails', newVal);
              }}
              format={'MM/DD/YYYY HH:mm:ss'}
              style={{ width: '100%' }}
              showTime
              placeholder="Select date and time"
              disabled={isDisabled}
              data-testid="time-of-event-select"
            />
          </Col>
          <Col span={12}>
            <label>Weight of Vehicle:</label>
            <Input
              value={initialFormValues?.driverReportDetails?.weightOfVehicle}
              onChange={(e) => {
                const newVal = Object.assign(
                  {},
                  initialFormValues.driverReportDetails,
                  {
                    weightOfVehicle: e.target.value,
                  }
                );
                handleChange(newVal);
                setInitialFormValue('driverReportDetails', newVal);
              }}
              style={{ width: '100%' }}
              disabled={isDisabled}
              data-testid="weight-of-vehicle-input"
            />
          </Col>
          <Col span={12}>
            <label>Driver Notes:</label>
            <Input
              value={initialFormValues?.driverReportDetails?.driverNotes}
              onChange={(e) => {
                const newVal = Object.assign(
                  {},
                  initialFormValues.driverReportDetails,
                  {
                    driverNotes: e.target.value,
                  }
                );
                handleChange(newVal);
                setInitialFormValue('driverReportDetails', newVal);
              }}
              style={{ width: '100%' }}
              disabled={isDisabled}
              data-testid="driver-notes-input"
            />
          </Col>
          <Col span={12}>
            <label>Billing Through:</label>
            <Select
              value={initialFormValues?.driverReportDetails?.billingThrough}
              onChange={(value) => {
                const newVal = Object.assign(
                  {},
                  initialFormValues.driverReportDetails,
                  {
                    billingThrough: value,
                  }
                );
                handleChange(newVal);
                setInitialFormValue('driverReportDetails', newVal);
              }}
              style={{ width: '100%' }}
              disabled={isDisabled}
              data-testid="billing-through-select"
            >
              <Select.Option
                key={'National account, reference the DR# 0372704'}
              >
                National account, reference the DR# 0372704
              </Select.Option>
              <Select.Option
                key={'National Account, Bridgestone (account number 956315)'}
              >
                National Account, Bridgestone (account number 956315)
              </Select.Option>
              <Select.Option
                key={'National Accounts - Bridgestone (Account #957128)'}
              >
                National Accounts - Bridgestone (Account #957128)
              </Select.Option>
            </Select>
          </Col>
        </Row>
      )}

      <Row className="limit-size-tablets" style={{ marginTop: '20px' }}>
        <Col xs={24} sm={24} lg={24}>
          <FormLegend>Upload Content</FormLegend>
          <ImageUploader
            childRef={(ref) => {
              setImageUploaderContainerRef(ref);
            }}
            onImageUploaded={(file, additionalData) => {
              handleChange();
              handleImageUpload(file, additionalData);
            }}
            wo_files={
              currentWorkOrder?.wo_files || {
                wo_images: wo_images || [],
                wo_documents: wo_documents || [],
              }
            }
            forScan={forScan}
          />
          <div style={documentUploaderStyle}>
            <DocumentUploader
              childRef={(ref) => {
                setDocumentUploaderContainerRef(ref);
              }}
              onFileUploaded={(file, additionalData) => {
                handleChange();
                handleFileUpload(file, additionalData);
              }}
              overrideUploadProps={{
                onPreview: handleDocumentPreview,
              }}
              customClass="document-uploader-with-preview"
              wo_files={
                currentWorkOrder?.wo_files || {
                  wo_images: wo_images || [],
                  wo_documents: wo_documents || [],
                }
              }
              forScan={forScan}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default WorkOrderOptionalInformation;
